import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Check, Star, Download, LogOut, Shield } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr } from "@/lib/api";

export default function Account() {
  const { user, refresh, logout } = useAuth();
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState("");
  const nav = useNavigate();

  useEffect(() => { api.get("/config/plans").then(({ data }) => setPlans(data.plans)).catch(() => {}); }, []);

  const loadRzp = () => new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

  const subscribe = async (id) => {
    setBusy(id);
    try {
      const ok = await loadRzp();
      if (!ok) { toast.error("Couldn't load Razorpay. Check your connection."); setBusy(""); return; }
      const { data } = await api.post("/payments/create-order", { plan_id: id });
      const rzp = new window.Razorpay({
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        order_id: data.order_id,
        name: "DecodeKid",
        description: `${data.plan.name} subscription`,
        prefill: { name: user.first_name || "", email: user.email },
        theme: { color: "#B04A39" },
        handler: async (resp) => {
          try {
            await api.post("/payments/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            await refresh();
            toast.success("Payment successful — you're now a Plus member!");
          } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
          finally { setBusy(""); }
        },
        modal: { ondismiss: () => setBusy("") },
      });
      rzp.on("payment.failed", (r) => { toast.error(r.error?.description || "Payment failed. Please try again."); setBusy(""); });
      rzp.open();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); setBusy(""); }
  };
  const cancel = async () => {
    setBusy("cancel");
    try { await api.post("/subscribe/cancel"); await refresh(); toast.success("Subscription cancelled"); }
    catch { toast.error("Couldn't cancel"); }
    finally { setBusy(""); }
  };
  const exportData = async () => {
    try { const { data } = await api.get("/history"); const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }); const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = "decodekid-export.json"; a.click(); toast.success("Your data was exported"); }
    catch { toast.error("Export failed"); }
  };

  const isPaid = user.access_state === "paid";

  return (
    <div className="space-y-6 max-w-3xl" data-testid="account-page">
      <h1 className="font-serif text-[28px] sm:text-[34px] font-semibold">Account</h1>

      <div className="dk-card p-6 space-y-2">
        <span className="text-xs uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Profile</span>
        <div className="font-serif text-[22px]">{user.first_name || "Parent"}</div>
        <div className="text-sm" style={{ color: "var(--text-muted)" }}>{user.email}</div>
        <div className="badge-soft mt-1">{isPaid ? "Plus member" : user.access_state === "trial" ? "Free trial" : "Access ended"}</div>
      </div>

      <div className="dk-card p-6 space-y-4" data-testid="billing-section">
        <div className="flex items-center gap-2"><Star size={18} style={{ color: "var(--accent)" }} /><h2 className="font-serif text-[20px] font-medium">Subscription</h2></div>
        {isPaid ? (
          <div className="space-y-3">
            <p className="text-sm" style={{ color: "var(--text-muted)" }}>You're on the <b>{user.subscription.plan}</b> plan. Thank you for supporting DecodeKid.</p>
            <button className="btn btn-outline" disabled={busy === "cancel"} onClick={cancel} data-testid="cancel-sub-btn">{busy === "cancel" && <Loader2 size={16} className="animate-spin" />} Cancel subscription</button>
          </div>
        ) : (
          <>
            <p className="text-sm" style={{ color: "var(--text-muted)" }}>Choose a plan to keep every tool. Secure checkout by Razorpay — cards, UPI, netbanking and wallets.</p>
            <div className="grid sm:grid-cols-3 gap-4">
              {plans.map((p) => (
                <div key={p.id} className={`dk-card p-5 space-y-3 ${p.popular ? "ring-2" : ""}`} style={p.popular ? { borderColor: "var(--accent)" } : {}}>
                  <h3 className="font-serif text-[18px] font-medium">{p.name}</h3>
                  <div className="font-serif text-[28px] font-semibold">₹{p.price}<span className="text-sm font-normal" style={{ color: "var(--text-muted)" }}>/{p.interval}</span></div>
                  <button className="btn btn-primary w-full" disabled={busy === p.id} onClick={() => subscribe(p.id)} data-testid={`subscribe-${p.id}`}>{busy === p.id ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} Subscribe</button>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <div className="dk-card p-6 space-y-4" data-testid="data-privacy-section">
        <div className="flex items-center gap-2"><Shield size={18} style={{ color: "var(--sage)" }} /><h2 className="font-serif text-[20px] font-medium">Data &amp; privacy</h2></div>
        <p className="text-sm" style={{ color: "var(--text-muted)" }}>Your child data is encrypted with a key only your account can use. Export or leave any time.</p>
        <div className="flex flex-wrap gap-2">
          <button className="btn btn-outline" onClick={exportData} data-testid="export-data-btn"><Download size={16} /> Export my data</button>
          <button className="btn btn-ghost" onClick={() => { logout(); nav("/"); }} data-testid="account-logout-btn"><LogOut size={16} /> Log out</button>
        </div>
      </div>
    </div>
  );
}
